import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import MainLayout from '@/components/layout/MainLayout';
import SearchBar from '@/components/search/SearchBar';
import Filters from '@/components/search/Filters';
import ResultsTable, { SortField } from '@/components/search/ResultsTable';
import ResultsPagination from '@/components/search/ResultsPagination';
import { ExportButton } from '@/components/search/ExportButton';
import { useAccommodationExport } from '@/features/alojamientos/hooks/useAccommodationExport';
import { useAccommodationListExport } from '@/features/alojamientos/hooks/useAccommodationListExport';
import { useSearch, SortField as SearchSortField } from '@/hooks/useSearch';
import { useAccommodationSearch } from "@/features/alojamientos/hooks/useAccommodationCreate";
import { toast } from "@/hooks/use-toast";
import { Establishment } from '@/components/search/ResultsTable';
import { useRoleAccess } from '@/features/auth/hooks/useRoleAccess';
import { ROLES } from '@/features/auth/constants/roles';
import { Navigate } from 'react-router-dom';
import { AccommodationSearchParams } from '@/features/alojamientos/types/accomodation.types';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const SearchPage = () => {
  const { hasRole } = useRoleAccess();
  const { exportToExcel, isExporting } = useAccommodationExport();
  const { exportList, isExporting: isExportingList } = useAccommodationListExport();
  // React Hook Form instance
  const methods = useForm({
    defaultValues: {
      services: {
        spa: false,
        gym: false,
        wifi: false,
        breakfast: false,
        casino: false,
        petsAllowed: false,
        cafeteriaBar: false,
        restaurant: false,
        eventRoom: false,
        tv: false,
        airConditioning: false,
        heating: false,
        safeBox: false,
        laundryService: false,
        frontdesk24h: false,
        smokingArea: false
      },
      isAccessible: false
    }
  });

  // Get search functionality from custom hook
  const {
    searchQuery,
    filters,
    sortField,
    sortDirection,
    handleSearchChange,
    handleFilterChange,
    clearFilters,
    handleSortChange
  } = useSearch();
  
  // Reemplazar la variable local con un estado
  const [resultsCount, setResultsCount] = useState(0);
  const [results, setResults] = useState<Establishment[]>([]);

  // Estado para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(10); // Número de resultados por página

  const formValues = methods.watch();

  // Get accommodation search mutation
  const { mutate } = useAccommodationSearch();

  // Set initial form values based on filters
  useEffect(() => {
    // Set the isAccessible value based on the accessibility filter
    methods.setValue('isAccessible', filters.accessibility === 'Si');
  }, [filters.accessibility, methods]);

  // Agregar este useEffect para realizar la búsqueda inicial
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Se ejecuta solo cuando el componente se monta

  if (hasRole([ROLES.OBSERVATORIO_OBSERVADOR])) {
    return <Navigate to="/muestras" />;
  }

  // Calcular valores de paginación
  const totalPages = Math.ceil(resultsCount / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;

  // Mapea los campos de sortField del hook a los que espera ResultsTable
  const mapSortField = (field: string): string => {
    switch (field) {
      case 'name': return 'establishmentName';
      case 'category': return 'categoryId';
      case 'locality': return 'localityId';
      case 'phone': return 'phone';
      case 'status': return 'statusId';
      case 'fileNumber': return 'fileNumber';
      // Agrega más mapeos si es necesario
      default: return '';
    }
  };

  // Función para normalizar strings para comparación
  const normalize = (str: string) =>
    str
      .normalize('NFD')                 // separa letras de acentos (e.g. Á → A + ́)
      .replace(/[\u0300-\u036f]/g, '') // elimina los acentos
      .replace(/[^a-zA-Z0-9]/g, '')    // elimina espacios, puntos, guiones, etc.
      .toUpperCase();                  // ignora mayúsculas/minúsculas

  // Función para ordenar los resultados
  const sortResults = (
    data: Establishment[],
    field: string,
    direction: 'asc' | 'desc'
  ): Establishment[] => {
    if (!field) return data;
    const mappedField = mapSortField(field);
    return [...data].sort((a, b) => {
      let comparison = 0;
      switch (mappedField) {
        case 'establishmentName':
          comparison = normalize(a.establishmentName).localeCompare(normalize(b.establishmentName));
          break;
        case 'fileNumber':
          comparison = a.fileNumber.localeCompare(b.fileNumber);
          break;
        case 'categoryId':
          comparison = (a.categoryId ?? '').toString().localeCompare((b.categoryId ?? '').toString());
          break;
        case 'localityId':
          comparison = (a.localityId ?? '').toString().localeCompare((b.localityId ?? '').toString());
          break;
        case 'phone':
          comparison = (a.phone ?? '').localeCompare(b.phone ?? '');
          break;
        case 'statusId':
          comparison = (a.statusId ?? '').toString().localeCompare((b.statusId ?? '').toString());
          break;
        default:
          return 0;
      }
      return direction === 'asc' ? comparison : -comparison;
    });
  };

  // Calcular valores de paginación usando los resultados ordenados
  const sortedResults = sortResults(results, sortField, sortDirection);
  const currentResults = sortedResults.slice(startIndex, endIndex);

  // Manejador para cambiar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle applying filters
  const handleApplyFilters = () => {
    //applyFilters();
    handleSearch();
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    clearFilters();
    methods.reset();
  };


  // Adapt the handleSearchChange function to handle React events
  const handleInputSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e.target.value);
  };

  // Handle search
  const handleSearch = () => {
    // Separar "none" de los valores reales y determinar los flags
    const hasUnassignedType = filters.type.includes('none');
    const hasUnassignedCategory = filters.category.includes('none');
    
    // Filtrar "none" de los arrays antes de enviarlos
    const typeWithoutNone = filters.type.filter(id => id !== 'none');
    const categoryWithoutNone = filters.category.filter(id => id !== 'none');
    
    const combinedFilters: AccommodationSearchParams = {
      query: searchQuery,
      ...filters,
      type: typeWithoutNone.length > 0 ? typeWithoutNone : undefined,
      category: categoryWithoutNone.length > 0 ? categoryWithoutNone : undefined,
      services: formValues.services,
      accessibility: formValues.isAccessible ? 'Si' : '',
      ...(hasUnassignedType && { unassignedType: true }),
      ...(hasUnassignedCategory && { unassignedCategory: true }),
    };

    mutate(combinedFilters, {
      onSuccess: (response) => {
        console.log("Search successful:", response);
        const establishments = Array.isArray(response) ? response : [];
        setResults(establishments);
        setResultsCount(establishments.length);
        setCurrentPage(1); // Resetear a la primera página cuando se realiza una nueva búsqueda
      },
      onError: (error: unknown) => {
        console.error("Search error:", error);
        const errorMessage = error instanceof Error ? error.message : "Ocurrió un error en la búsqueda.";
        toast({
          title: "Error al buscar",
          description: errorMessage,
          variant: "destructive",
        });
        setResults([]);
        setResultsCount(0);
        setCurrentPage(1);
      },
    });
  };

  // Mapea el campo del hook al campo de la tabla
  const mapHookSortFieldToTable = (field: string): string => {
    switch (field) {
      case 'name': return 'establishmentName';
      case 'category': return 'categoryId';
      case 'locality': return 'localityId';
      case 'phone': return 'phone';
      case 'status': return 'statusId';
      case 'fileNumber': return 'fileNumber';
      // Agrega más mapeos si es necesario
      default: return '';
    }
  };

  // Mapea el campo de la tabla al campo del hook
  const mapTableSortFieldToHook = (field: string): string => {
    switch (field) {
      case 'establishmentName': return 'name';
      case 'categoryId': return 'category';
      case 'localityId': return 'locality';
      case 'phone': return 'phone';
      case 'statusId': return 'status';
      case 'fileNumber': return 'fileNumber';
      // Agrega más mapeos si es necesario
      default: return '';
    }
  };

  // Handler para el sort de la tabla
  const handleTableSortChange = (fieldFromTable: string) => {
    const mappedField = mapTableSortFieldToHook(fieldFromTable);
    handleSortChange(mappedField as SearchSortField);
  };

  // Preparar filtros para exportación
  const getExportFilters = (): AccommodationSearchParams => {
    // Separar "none" de los valores reales y determinar los flags
    const hasUnassignedType = filters.type.includes('none');
    const hasUnassignedCategory = filters.category.includes('none');
    
    // Filtrar "none" de los arrays antes de enviarlos
    const typeWithoutNone = filters.type.filter(id => id !== 'none');
    const categoryWithoutNone = filters.category.filter(id => id !== 'none');
    
    return {
      query: searchQuery,
      ...filters,
      type: typeWithoutNone.length > 0 ? typeWithoutNone : undefined,
      category: categoryWithoutNone.length > 0 ? categoryWithoutNone : undefined,
      services: formValues.services,
      accessibility: formValues.isAccessible ? 'Si' : '',
      ...(hasUnassignedType && { unassignedType: true }),
      ...(hasUnassignedCategory && { unassignedCategory: true }),
    };
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Búsqueda de establecimientos</h1>
            <p className="text-gray-600 mt-2">Busque y filtre alojamientos por múltiples criterios</p>
          </div>
        </div>
        
        {/* Search Bar */}
        <SearchBar 
          searchQuery={searchQuery}
          onSearchChange={handleInputSearchChange}
          onSearch={handleSearch}
        />
        
        {/* Filters */}
        <Filters 
          filters={filters}
          methods={methods}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
        
        {/* Results Section */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>
                Resultados de la búsqueda
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({resultsCount} alojamiento{resultsCount !== 1 ? 's' : ''} encontrado{resultsCount !== 1 ? 's' : ''})
                </span>
              </CardTitle>
              <div className="flex items-center gap-2">
                <ExportButton
                  onExport={() => exportList()}
                  isExporting={isExportingList}
                  label="Descargar Tarifario"
                />
                <ExportButton
                  onExport={() => exportToExcel(getExportFilters())}
                  isExporting={isExporting}
                  label="Descargar Excel"
                  disabled={resultsCount === 0}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <ResultsTable 
                establishments={currentResults} 
                sortField={mapHookSortFieldToTable(sortField) as SortField}
                sortDirection={sortDirection}
                onSort={handleTableSortChange}
              />
              {resultsCount > 0 && (
                <div className="mt-4">
                  <ResultsPagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalResults={resultsCount}
                    resultsPerPage={resultsPerPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SearchPage;
